"use client";

import { useEffect } from "react";
import { Check, ChevronDown, Loader2, MapPin } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { createLocationOptionsStore, type LocationOption } from "@/stores/discovery-stores";
import { usePreferencesStore } from "@/stores/preferences-store";
import { useScopedStore } from "@/stores/scoped-store";
import { cn } from "@/lib/utils";

type SuburbResponse = {
  suburbs?: LocationOption[];
};

export function SuburbSelector({
  open,
  onOpenChange,
  compact = false
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  compact?: boolean;
}) {
  const selectedSuburb = usePreferencesStore((state) => state.selectedSuburb);
  const setSelectedSuburb = usePreferencesStore((state) => state.setSelectedSuburb);
  const { options, status, error, setLoading, setSuccess, setError } = useScopedStore(
    "location-options:public-suburbs",
    createLocationOptionsStore,
    (state) => state
  );

  useEffect(() => {
  if (status !== "idle") return;

  setLoading();

  fetch("/api/suburbs", { credentials: "include" })
    .then(async (response) => {
      if (!response.ok) throw new Error("Could not load suburbs.");
      return response.json() as Promise<SuburbResponse>;
    })
    .then((payload) => {
      setSuccess(payload.suburbs ?? []);
    })
    .catch((fetchError) => {
      setError(fetchError instanceof Error ? fetchError.message : "Could not load suburbs.");
    });
}, [status, setLoading, setSuccess, setError]);

  const label = selectedSuburb?.name ?? "Sydney, AU";
  const chooseSuburb = (suburb: LocationOption | null) => {
    setSelectedSuburb(suburb ? { id: suburb.id, slug: suburb.slug, name: suburb.name } : null);
    onOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "group inline-flex max-w-full items-center gap-2 bg-transparent px-0 py-0 text-[11px] font-black uppercase tracking-[0.14em] text-[#050505] shadow-none transition-all hover:bg-transparent hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/25",
            compact ? "min-h-11 w-full justify-between bg-transparent px-0 text-foreground shadow-none hover:bg-transparent" : "max-w-[13.5rem]"
          )}
          aria-label="Choose suburb"
        >
          <span className="inline-flex min-w-0 items-center gap-2">
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#ff3338]/10 text-[#ff3338] transition-colors group-hover:bg-[#ff3338] group-hover:text-white">
              <MapPin className="h-3.5 w-3.5" />
            </span>
            <span className="min-w-0 truncate">{label}</span>
          </span>
          <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="local-buzz-theme w-80 rounded-xl border-border bg-card p-2 text-foreground shadow-lift">
        <div className="px-3 pb-2 pt-3">
          <p className="text-[10px] font-black uppercase tracking-[0.22em] text-brand">Local recommendations</p>
          <h2 className="mt-1 font-display text-2xl leading-none">Choose your suburb</h2>
          <p className="mt-1 text-xs text-muted-foreground">We'll tune recommendations and directory results to this area.</p>
        </div>
        <div className="suburb-selector-scroll max-h-72 overflow-y-auto p-1 pr-2">
          <button
            type="button"
            onClick={() => chooseSuburb(null)}
            className={cn(
              "flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition-colors hover:bg-muted",
              !selectedSuburb && "bg-brand text-white hover:bg-brand"
            )}
          >
            All Sydney
            {!selectedSuburb ? <Check className="h-4 w-4" /> : null}
          </button>
          {status === "loading" ? (
            <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading suburbs...
            </div>
          ) : null}
          {status === "error" ? <p className="px-3 py-4 text-sm text-destructive">{error}</p> : null}
          {options.map((suburb) => {
            const active = selectedSuburb?.id === suburb.id;
            return (
              <button
                key={suburb.id}
                type="button"
                onClick={() => chooseSuburb(suburb)}
                className={cn(
                  "flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition-colors hover:bg-muted",
                  active && "bg-brand text-white hover:bg-brand"
                )}
              >
                <span>
                  {suburb.name}
                  {suburb.postcode ? <span className="ml-1 text-xs opacity-65">{suburb.postcode}</span> : null}
                </span>
                {active ? <Check className="h-4 w-4" /> : null}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
